import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Space, Tooltip, Modal, Select, message, Progress } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import moment from 'moment';
import api from '../utils/axios';

const { Option } = Select;

const EmployeeList = ({ 
  employees, 
  loading, 
  pagination, 
  onPageChange, 
  onView, 
  onRefresh,
  currentUser
}) => {
  const [editingRecruiter, setEditingRecruiter] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [statusEditModalVisible, setStatusEditModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newStatus, setNewStatus] = useState('');

  // 获取用户列表（带重试机制）
  const fetchUsers = async (retryCount = 0) => {
    try {
      setLoadingUsers(true);
      const response = await api.get('/users');
      
      if (response.data.success) {
        const userData = response.data.data;
        if (Array.isArray(userData)) {
          setUsers(userData);
          console.log('✅ 用户列表获取成功，共', userData.length, '个用户');
        } else {
          console.warn('用户数据格式不正确:', userData);
          setUsers([]);
        }
      } else {
        console.warn('获取用户列表失败:', response.data.message);
        // 只在第一次失败时显示错误消息，避免重复显示
        if (retryCount === 0) {
          message.error(response.data.message || '获取用户列表失败');
        }
        setUsers([]);
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      if (error.response) {
        console.error('响应状态:', error.response.status);
        console.error('响应数据:', error.response.data);
      }
      
      // 重试逻辑：最多重试2次
      if (retryCount < 2) {
        console.log(`🔄 重试获取用户列表 (${retryCount + 1}/2)`);
        setTimeout(() => {
          fetchUsers(retryCount + 1);
        }, 1000 * (retryCount + 1)); // 递增延迟：1秒、2秒
      } else {
        // 只在最终失败时显示错误消息
        message.error('获取用户列表失败');
        setUsers([]);
      }
    } finally {
      if (retryCount === 0) {
        setLoadingUsers(false);
      }
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // 修改招聘人事
  const handleUpdateRecruiter = async (employeeId, newRecruiterId) => {
    try {
      await api.put(`/employees/${employeeId}/recruiter`, {
        created_by: newRecruiterId
      });
      message.success('招聘人事修改成功');
      setEditingRecruiter(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('修改招聘人事失败:', error);
      message.error(error.response?.data?.message || '修改招聘人事失败');
    }
  };

  // 处理状态编辑
  const handleStatusEdit = (employee) => {
    setEditingEmployee(employee);
    setNewStatus(employee.status);
    setStatusEditModalVisible(true);
  };

  // 保存状态修改
  const handleStatusSave = async () => {
    if (!editingEmployee || !newStatus) return;
    
    try {
      await api.patch(`/employees/${editingEmployee.id}/status`, {
        status: newStatus
      });
      message.success('员工状态修改成功');
      
      // 如果员工状态变为已离职，关闭该员工的所有任务
      if (newStatus === 'resigned') {
        try {
          await api.post(`/employee-tasks/employees/${editingEmployee.id}/tasks/close-all`, {
            status: 'abnormal',
            reason: '离职结束',
            end_date: new Date().toISOString()
          });
          message.success('员工任务已全部关闭');
        } catch (taskError) {
          console.error('关闭员工任务失败:', taskError);
          message.warning('员工状态已更新，但任务关闭失败');
        }
      }
      
      setStatusEditModalVisible(false);
      setEditingEmployee(null);
      setNewStatus('');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('修改员工状态失败:', error);
      message.error(error.response?.data?.message || '修改员工状态失败');
    }
  };

  // 取消状态编辑
  const handleStatusCancel = () => {
    setStatusEditModalVisible(false);
    setEditingEmployee(null);
    setNewStatus('');
  };

  // 检查是否是管理员
  const isAdmin = currentUser && (currentUser.role === 'super_admin' || currentUser.role === 'hr_manager');
  // 计算在职天数
  const calculateWorkDays = (hireDate) => {
    if (!hireDate) return '-';
    const hire = moment(hireDate);
    const now = moment();
    const days = now.diff(hire, 'days');
    return `${days}天`;
  };

  // 计算合同剩余天数
  const calculateContractDays = (contractEndDate, contractStatus) => {
    if (!contractEndDate) return null;
    
    const endDate = moment(contractEndDate);
    const now = moment();
    const days = endDate.diff(now, 'days');
    
    if (contractStatus === 'expired') {
      return { text: '已过期', color: '#ff4d4f', days: Math.abs(days) };
    } else if (contractStatus === 'expiring_soon') {
      return { text: `${days}天`, color: '#faad14', days };
    } else {
      return { text: `${days}天`, color: '#52c41a', days };
    }
  };

  // 获取状态颜色和文本
  const getStatusInfo = (status) => {
    switch (status) {
      case 'probation':
        return { color: 'orange', text: '试用期' };
      case 'regular':
        return { color: 'green', text: '正式员工' };
      case 'resigning':
        return { color: 'orange', text: '离职中' };
      case 'resigned':
      case 'terminated':
        return { color: 'red', text: '已离职' };
      default:
        return { color: 'default', text: status };
    }
  };

  // 获取在职状态
  const getEmploymentStatus = (status) => {
    switch (status) {
      case 'probation':
      case 'regular':
      case 'resigning':
        return { color: 'green', text: '在职' };
      case 'resigned':
      case 'terminated':
        return { color: 'red', text: '离职' };
      default:
        return { color: 'default', text: '未知' };
    }
  };

  // 计算员工合规进度
  const calculateComplianceProgress = (employee) => {
    if (!employee) return 0;

    // 定义需要检查的字段和权重
    const fields = [
      // 基本信息字段（权重 1）
      { name: 'name', weight: 1 },
      { name: 'gender', weight: 1 },
      { name: 'birth_date', weight: 1 },
      { name: 'phone', weight: 1 },
      { name: 'email', weight: 1 },
      { name: 'id_card', weight: 1 },
      { name: 'address', weight: 1 },
      { name: 'emergency_contact_name', weight: 1 },
      { name: 'emergency_contact_phone', weight: 1 },
      { name: 'emergency_contact_relation', weight: 1 },
      
      // 工作信息字段（权重 1）
      { name: 'department_id', weight: 1 },
      { name: 'position_id', weight: 1 },
      { name: 'hire_date', weight: 1 },
      { name: 'work_location', weight: 1 },
      
      // 证件与收款信息字段（权重 1）
      { name: 'id_card_front', weight: 1 },
      { name: 'id_card_back', weight: 1 },
      { name: 'bank_card_number', weight: 1 },
      { name: 'bank_name', weight: 1 },
      { name: 'wechat_qr_code', weight: 1 },
      { name: 'alipay_qr_code', weight: 1 },
      
      // 合同信息字段（权重 1）
      { name: 'contract_status', weight: 1 },
      
      // 薪资信息字段（权重 1）
      { name: 'salary_level', weight: 1 },
    ];

    // 计算总权重和已完成权重
    const totalWeight = fields.reduce((sum, field) => sum + field.weight, 0);
    let completedWeight = 0;

    fields.forEach(field => {
      const value = employee[field.name];
      if (value !== undefined && value !== null && value !== '') {
        completedWeight += field.weight;
      }
    });

    // 计算进度百分比
    const progress = Math.round((completedWeight / totalWeight) * 100);
    return progress;
  };

  // 获取进度条颜色
  const getProgressColor = (progress) => {
    if (progress >= 90) return '#52c41a'; // 绿色
    if (progress >= 70) return '#73d13d'; // 浅绿色
    if (progress >= 50) return '#faad14'; // 黄色
    if (progress >= 30) return '#fa8c16'; // 橙色
    return '#ff4d4f'; // 红色
  };

  const columns = [
    {
      title: '工号',
      dataIndex: 'employee_no',
      key: 'employee_no',
      width: 120,
      fixed: 'left',
      render: (text, record) => (
        <a
          onClick={() => onView(record)}
          style={{
            color: '#1890ff',
            cursor: 'pointer',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => {
            e.target.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.target.style.textDecoration = 'none';
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      fixed: 'left',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.lead_source_name && (
            <div style={{ fontSize: '11px', color: '#52c41a', marginTop: '2px' }}>
              📍 来源: {record.lead_source_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '招聘人事',
      key: 'recruiter',
      width: 120,
      render: (_, record) => {
        if (editingRecruiter && editingRecruiter.id === record.id) {
          return (
            <Select
              style={{ width: '100%' }}
              placeholder="选择招聘人事"
              loading={loadingUsers}
              defaultValue={record.created_by}
              onSelect={(value) => handleUpdateRecruiter(record.id, value)}
              onBlur={() => setEditingRecruiter(null)}
              autoFocus
            >
              <Option value={null}>未设置</Option>
              {Array.isArray(users) && users.map(user => (
                <Option key={user.id} value={user.id}>{user.name || user.username}</Option>
              ))}
            </Select>
          );
        }
        
        return (
          <div>
            <div>{record.recruiter_name || '未设置'}</div>
            {isAdmin && (
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => setEditingRecruiter({ id: record.id })}
                style={{ padding: '0 4px', fontSize: '12px', color: '#1890ff' }}
              >
                修改
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: '合规进度',
      dataIndex: 'id',
      key: 'compliance_progress',
      width: 120,
      render: (_, record) => {
        const progress = calculateComplianceProgress(record);
        const color = getProgressColor(progress);
        return (
          <Progress 
            percent={progress} 
            size="small" 
            strokeColor={color}
            status={progress === 100 ? 'success' : 'active'}
            format={(percent) => `${percent}%`}
          />
        );
      },
    },
    {
      title: '部门',
      dataIndex: 'department_name',
      key: 'department_name',
      width: 141,
      render: (text) => text || '-',
    },
    {
      title: '职位',
      dataIndex: 'position_name',
      key: 'position_name',
      width: 141,
      render: (text) => text || '-',
    },
    {
      title: '入职日期',
      dataIndex: 'hire_date',
      key: 'hire_date',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '在职天数',
      key: 'work_days',
      width: 120,
      sorter: (a, b) => {
        const getDays = (date) => (date ? moment().diff(moment(date), 'days') : Number.POSITIVE_INFINITY);
        return getDays(a.hire_date) - getDays(b.hire_date);
      },
      sortDirections: ['descend', 'ascend'],
      render: (_, record) => {
        const workDays = calculateWorkDays(record.hire_date);
        const contractInfo = calculateContractDays(record.contract_end_date, record.contract_status);
        
        return (
          <div>
            <div>{workDays}</div>
            {contractInfo && (
              <div style={{ 
                fontSize: '12px', 
                color: contractInfo.color,
                marginTop: '2px'
              }}>
                合同剩余: {contractInfo.text}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '试用期结束',
      dataIndex: 'probation_end_date',
      key: 'probation_end_date',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '转正日期',
      dataIndex: 'regular_date',
      key: 'regular_date',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '在职状态',
      dataIndex: 'status',
      key: 'employment_status',
      width: 100,
      render: (status) => {
        const employmentStatus = getEmploymentStatus(status);
        return <Tag color={employmentStatus.color}>{employmentStatus.text}</Tag>;
      },
    },
    {
      title: '直属上级',
      dataIndex: 'manager_name',
      key: 'manager_name',
      width: 100,
      render: (text) => text || '-',
    },
    {
      title: '工作地点',
      dataIndex: 'work_location',
      key: 'work_location',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 150,
      render: (text) => text || '-',
    },
    {
      title: '添加时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
      sortDirections: ['descend', 'ascend'],
      render: (text) => {
        if (!text) return '-';
        return (
          <div>
            <div>{moment(text).format('YYYY-MM-DD')}</div>
            <div style={{ fontSize: '11px', color: '#999' }}>
              {moment(text).format('HH:mm')}
            </div>
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              onClick={() => onView(record)}
            >
              查看
            </Button>
          </Tooltip>
          {isAdmin && (
            <Tooltip title="编辑状态">
              <Button
                type="text"
                size="small"
                onClick={() => handleStatusEdit(record)}
              >
                状态
              </Button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="employee-list">
      <Table
        columns={columns}
        dataSource={employees}
        loading={loading}
        pagination={{
          ...pagination,
          onChange: onPageChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        rowKey="id"
        scroll={{ x: 1500 }}
        size="middle"
        bordered
        className="custom-table"
      />
      
      {/* 状态编辑模态框 */}
      <Modal
        title="编辑员工状态"
        open={statusEditModalVisible}
        onOk={handleStatusSave}
        onCancel={handleStatusCancel}
        okText="保存"
        cancelText="取消"
      >
        {editingEmployee && (
          <div>
            <p><strong>员工姓名：</strong>{editingEmployee.name}</p>
            <p><strong>当前状态：</strong>
              <Tag color={getStatusInfo(editingEmployee.status).color}>
                {getStatusInfo(editingEmployee.status).text}
              </Tag>
            </p>
            <p><strong>新状态：</strong></p>
            <Select
              value={newStatus}
              onChange={setNewStatus}
              style={{ width: '100%' }}
            >
              <Option value="probation">试用期</Option>
              <Option value="regular">正式员工</Option>
              <Option value="resigning">离职中</Option>
              <Option value="resigned">已离职</Option>
            </Select>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmployeeList;
